import './BMPModal.scss';
import { Button, Modal, Table } from "semantic-ui-react";
import Wave2 from "../../../images/modalWave2.svg";
import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import { SidebarTypes } from '../../../core/SidebarComponent';
import { BadValueBmpViewModel } from '../../../services/BmpService';
import { LookupsContext } from '../../../lookups/LookupFactory';

export interface IBMPModalProps {
    modalOpen: boolean;
    setModalOpen: Dispatch<SetStateAction<boolean>>;
    badBMPs: BadValueBmpViewModel[] | null;
    onInfoClick: (bmpId: number, viewType: SidebarTypes) => Promise<void>
}

// Helper function to group BMPs by name
const groupBMPsByName = (bmps: BadValueBmpViewModel[]): Map<string, BadValueBmpViewModel[]> => {
    const grouped = new Map<string, BadValueBmpViewModel[]>();

    bmps.forEach(bmp => {
        const existing = grouped.get(bmp.bmpName) || [];
        grouped.set(bmp.bmpName, [...existing, bmp]);
    });

    return grouped;
};

export const BMPModal = (props: IBMPModalProps) => {
    const [hoveredBmpName, setHoveredBmpName] = useState<string | null>(null);
    const [lookups] = useContext(LookupsContext);

    // Group BMPs by name for the grouped table
    const groupedBMPs = useMemo(() => {
        if (!props.badBMPs || props.badBMPs.length === 0) {
            return new Map<string, BadValueBmpViewModel[]>();
        }
        return groupBMPsByName(props.badBMPs);
    }, [props.badBMPs]);

    const handleBMPClick = (bmpId: number) => {
        props.onInfoClick(bmpId, "PrettyProjectPage");
        props.setModalOpen(false);
    };

    const getColorClassForId = (bmpId: number) => {
        const hash = bmpId % 2; // 0 or 1 → perfect for alternating
        return hash === 0 ? "group-color-a" : "group-color-b";
    };

    // Render the grouped table
    const renderGroupedTable = () => (
        <div className="bmp-table-container">
            {/* Sticky Header Table */}
            <Table celled compact className="bmp-table-header">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell className="width-lg">BMP Name</Table.HeaderCell>
                        <Table.HeaderCell className="width-sm">Property</Table.HeaderCell>
                        <Table.HeaderCell className="width-sm">Value</Table.HeaderCell>
                        <Table.HeaderCell className="width-lg">Valid Range</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
            </Table>
            {/* Scrollable Body Container */}
            <div className="bmp-table-body-wrapper">
                <Table
                    celled
                    compact
                    className="bmp-table-body"
                >
                    <Table.Body>
                        {groupedBMPs.size > 0
                            ? Array.from(groupedBMPs.entries()).map(([bmpName, bmps]) => {
                                return bmps.map((bmp, index) => (
                                    <Table.Row
                                        key={`${bmp.bmpId}-${bmp.bmpName}-${bmp.lutBmpPropertyId}`}
                                        className={`clickable-row ${getColorClassForId(bmp.bmpId)}`}
                                        onClick={() => handleBMPClick(bmp.bmpId)}
                                    >
                                        {/* Only show BMP name in first row of each group */}
                                        {index === 0 && (
                                            <Table.Cell
                                                rowSpan={bmps.length}
                                                className={`width-lg grouped-bmp-name ${getColorClassForId(bmp.bmpId)} ${hoveredBmpName === bmpName ? 'is-hovered' : ''
                                                    }`}
                                                onMouseEnter={() => setHoveredBmpName(bmpName)}
                                                onMouseLeave={() => setHoveredBmpName(null)}
                                            >
                                                {bmpName}
                                            </Table.Cell>
                                        )}
                                        <Table.Cell
                                            onMouseEnter={() => setHoveredBmpName(bmpName)}
                                            onMouseLeave={() => setHoveredBmpName(null)}
                                            className={`width-sm border-left ${hoveredBmpName === bmpName ? 'is-hovered' : ''} ${getColorClassForId(bmp.bmpId)}`}
                                        >
                                            {lookups.getBmpPropertyNameById(bmp.lutBmpPropertyId)}
                                        </Table.Cell>
                                        <Table.Cell
                                            onMouseEnter={() => setHoveredBmpName(bmpName)}
                                            onMouseLeave={() => setHoveredBmpName(null)}
                                            className={`width-sm ${hoveredBmpName === bmpName ? 'is-hovered' : ''} ${getColorClassForId(bmp.bmpId)}`}
                                        >
                                            {bmp.value}
                                        </Table.Cell>
                                        <Table.Cell
                                            onMouseEnter={() => setHoveredBmpName(bmpName)}
                                            onMouseLeave={() => setHoveredBmpName(null)}
                                            className={`width-lg ${hoveredBmpName === bmpName ? 'is-hovered' : ''} ${getColorClassForId(bmp.bmpId)}`}
                                        >
                                            {bmp.validRange}
                                        </Table.Cell>
                                    </Table.Row>
                                ));
                            })
                            : (
                                <Table.Row>
                                    <Table.Cell
                                        colSpan={4}
                                        style={{ textAlign: "center" }}
                                    >
                                        No BMPs found.
                                    </Table.Cell>
                                </Table.Row>
                            )
                        }
                    </Table.Body>
                </Table>
            </div>
        </div>
    );

    return (
        <Modal className="bmp-modal"
            size="large"
            centered={true}
            open={props.modalOpen}
            onClose={() => props.setModalOpen(false)}
        >
            <Modal.Header>
                The following BMPs have invalid properties. This will prevent capture calculations from working properly.
            </Modal.Header>
            <Modal.Content className="bmp-modal-content">
                {renderGroupedTable()}
            </Modal.Content>
            <Modal.Actions>
                <div className="wave-div">
                    <img src={Wave2} alt="wave" className="wave" />
                </div>
                <div className="modal-btns">
                    <Button
                        className="primaryBtn"
                        onClick={() => {
                            props.setModalOpen(false);
                        }}
                    >
                        OK
                    </Button>
                </div>
            </Modal.Actions>
        </Modal>
    )
}
